import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { EDITOR_TOOLS } from '@editor/config/constants'
import { PLANE_SIDE_COLOR } from '@editor/config/styles'
import { EditorContext } from '@editor/contexts/Editor'
import { CameraProfileState } from '@editor/interfaces/camera'
import { MinimumAreaBoundary } from '@editor/interfaces/diagram'
import { CanvasConfig } from '@editor/interfaces/editor'
import { GridType, IntervalsAxisConfig, IntervalsConfig } from '@editor/interfaces/inspectionItemGrid'
import {
  CircleAnchorProps,
  LabelProps,
  LineStyle,
  PlaneSide,
  PointArray,
  Polygon,
  PolygonPlaneMeshProps,
  PolylineProps,
} from '@editor/interfaces/interfaces'
import { addDimmedElementId, removeDimmedElementId } from '@editor/pages/projects/editor/store/editor'
import { selectIsAdjustingLabelColors } from '@editor/pages/projects/editor/store/selectors'
import { getCameraProfile } from '@editor/services/Camera'
import { minAreaRectangleOfPolygon } from '@editor/services/MinimumRectangle'
import { calculateBoundaryDirectionVectors } from '@editor/services/PlaneDiagram'
import { getCentroidOfPolygon3d, isPointInsidePolygon3D, transform2Dto3D } from '@editor/services/Points'
import { findLowerPlane, findNormalTowardsB, findUpperPlane } from '@editor/services/Shape'
import { getDistanceLabel, isHoverSelected, millimeterToMeter } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { Matrix4, Plane, Quaternion, Vector2, Vector3 } from 'three'

import { GRID_POINT_MINIMUM_INTERVAL } from '../constants'
import { updateWorkingEdgePlanes, updateWorkingGridPoint, updateWorkingGridPoints } from '../store'
import { EdgeVector, generateEdgePlanes, generateGridPoints, validateIntervalConfig } from '../utils'

const useMainCanvas = (): CanvasConfig => {
  // Context
  const { cameraRef, arcballControlsRef, pointSelectionObjectIdsRef } = useContext(EditorContext)

  // Store
  const dispatch = useAppDispatch()
  const inspectionItems = useAppSelector((state) => state.inspectionItems.items)
  const shapes = useAppSelector((state) => state.shapes.shapes)

  const cameraProfile = useAppSelector((state) => state.toolCameraProfile.cameraProfile)
  const hoveredElementId = useAppSelector((state) => state.toolUtil.core.hoveredElementId)
  const selectedElementIds = useAppSelector((state) => state.toolUtil.core.selectedElementIds)
  const hiddenElementIds = useAppSelector((state) => state.toolUtil.core.hiddenElementIds)
  const editingElementId = useAppSelector((state) => state.toolUtil.core.editingElementId)

  const isAdjustingLabelColors = useAppSelector(selectIsAdjustingLabelColors)
  const gridDisplaySettings = useAppSelector((state) => state.editorSettings.display.grid)
  const currentAdjustingKey = useAppSelector((state) => state.toolUtil.core.currentAdjustingKey)

  const selectedTool = useAppSelector((state) => state.editor.selectedTool)
  const prevSelectedTool = useAppSelector((state) => state.editor.prevSelectedTool)
  const grids = useAppSelector((state) => state.toolGrid.grids)
  const intervals = useAppSelector((state) => state.toolGrid.intervals)
  const workingGridPoints = useAppSelector((state) => state.toolGrid.workingGridPoints)
  const workingEdgePlanes = useAppSelector((state) => state.toolGrid.workingEdgePlanes)
  const shownWorkingEdgePlanes = useAppSelector((state) => state.toolGrid.shownWorkingEdgePlanes)
  const highlightedWorkingGrid = useAppSelector((state) => state.toolGrid.highlightedWorkingGrid)
  const hoveredGridPoint = useAppSelector((state) => state.toolGrid.hoveredGridPoint)

  // State
  const [existingDistanceLabels, setExistingDistanceLabels] = useState<Record<string, PolylineProps[]>>({})
  const [workingDistanceLabels, setWorkingDistanceLabels] = useState<Record<string, PolylineProps[]>>({})
  const [warningLabels, setWarningLabels] = useState<LabelProps[]>([])

  // Refs
  const threePlanesRef = useRef<Record<string, Plane>>({}) // map of shape_id to Plane instance
  const shapePointsV3Ref = useRef<Record<string, Vector3[]>>({}) // map of shape_id to array of Vector3 points
  const dimmedShapeIdRef = useRef<string>(undefined)

  // Flags
  const isToolSelected = selectedTool === EDITOR_TOOLS.GRID

  // State checks
  const isEditing = useMemo(() => (id: string) => id === editingElementId, [editingElementId])

  /**
   * Generate distance labels for grids retrieved from API.
   */
  useEffect(() => {
    const newDistanceLabels: Record<string, PolylineProps[]> = {}

    // Generate distance labels
    grids.forEach((item) => {
      newDistanceLabels[item.volume_id!] =
        item.grid?.list_distances
          .filter(() => !intervals[item.volume_id!])
          .map<PolylineProps>((distance, index) => {
            const isHovered =
              hoveredGridPoint?.gridId === item.inspection_item_id && hoveredGridPoint?.pointIndex === index

            // Hide if invisible however if highlighted, show with low opacity
            const isInvisble =
              (hiddenElementIds.includes(item.inspection_item_id!) ||
                hiddenElementIds.includes(`panel-grid-point-${item.inspection_item_id}-${index}`)) &&
              hoveredElementId !== item.inspection_item_id
            let opacity = isInvisble ? 0 : 1
            if (isHovered && isInvisble) {
              opacity = 0.6
            }

            return {
              id: `grid-point-${item.volume_id}-${distance.position_on_grid_point.join('-')}`,
              points: distance.distance
                ? [distance.position_on_grid_point, distance.position_on_projected_point]
                : [distance.position_on_grid_point],
              anchorScale: isHovered ? 1.5 : undefined,
              label:
                (isHoverSelected(item.inspection_item_id!, hoveredElementId, selectedElementIds) || isHovered) &&
                gridDisplaySettings.label.visible
                  ? {
                      label: `${distance.name}: ${getDistanceLabel(distance.distance || 0)}`,
                      styles: {
                        borderColor: isHovered ? '#fff' : undefined,
                        color: gridDisplaySettings.label.text_color,
                        backgroundColor: gridDisplaySettings.label.bg_color,
                      },
                      isAdjustingColors:
                        isAdjustingLabelColors &&
                        currentAdjustingKey !== undefined &&
                        ['grid.label.text_color', 'grid.label.bg_color'].includes(currentAdjustingKey),
                    }
                  : undefined,
              opacity,
              isInteractable: false,
              lineOutline: {},
              hideAnchors: true,
              vertexAnchors: {},
              topAnchorProps: {
                color: PLANE_SIDE_COLOR.upper,
              },
              bottomAnchorProps: {
                color: PLANE_SIDE_COLOR.lower,
              },
              anchorProps: {
                outlineColor: distance.distance ? undefined : 'yellow',
              },
            }
          }) || []
    })
    setExistingDistanceLabels(newDistanceLabels)

    // Generate warning labels
    if (gridDisplaySettings.error.label.visible) {
      setWarningLabels(
        grids
          .map<LabelProps[]>(
            (item) =>
              item.grid?.list_distances
                .filter(
                  (distance) =>
                    !distance.distance &&
                    !intervals[item.volume_id!] &&
                    isHoverSelected(item.inspection_item_id!, hoveredElementId, selectedElementIds),
                )
                .map((distance, index) => {
                  const isHovered =
                    hoveredGridPoint?.gridId === item.inspection_item_id && hoveredGridPoint?.pointIndex === index

                  // Hide if invisible however if highlighted, show with low opacity
                  const isInvisble =
                    hiddenElementIds.includes(item.inspection_item_id!) ||
                    hiddenElementIds.includes(`panel-grid-point-${item.inspection_item_id}-${index}`)
                  let opacity = isInvisble ? 0 : 1
                  if (isHovered && isInvisble) {
                    opacity = 0.6
                  }

                  return {
                    id: `grid-point-${item.volume_id}-${index}`,
                    point: distance.position_on_grid_point,
                    label: distance.name,
                    message: '底面に対応する点が見つかりません',
                    opacity,
                    styles: {
                      color: gridDisplaySettings.error.label.text_color,
                      backgroundColor: gridDisplaySettings.error.label.bg_color,
                    },
                    offset: [0, -18] as [number, number],
                  }
                }) || [],
          )
          .flat(),
      )
    } else {
      setWarningLabels([])
    }
  }, [
    grids,
    intervals,
    hoveredElementId,
    selectedElementIds,
    hiddenElementIds,
    hoveredGridPoint,
    inspectionItems,
    currentAdjustingKey,
    isAdjustingLabelColors,
    gridDisplaySettings,
  ])

  /**
   * Generate minimum rectangle boundaries for polygons.
   */
  const minimumRectangleBoundaries = useMemo(
    () =>
      (shapes.polygons || [])
        .filter((poly) => poly.plane_side === PlaneSide.UPPER)
        .map((poly) => ({
          polygon: poly,
          boundary: minAreaRectangleOfPolygon(poly.vertices, poly.positions),
        }))
        .filter((bundle) => bundle?.boundary) as { polygon: Polygon; boundary: MinimumAreaBoundary }[],
    [shapes],
  )

  /**
   * Get shapes of both top and bottom planes for both polygon and rectangle.
   */
  const getTopAndBottomShapes = useCallback(
    (interval: IntervalsConfig) => {
      const topShape = shapes.polygons?.find((poly) => poly.shape_id === interval.topPlaneId)
      const bottomShape = shapes.polygons?.find((poly) => poly.shape_id === interval.bottomPlaneId)

      return {
        topShape,
        bottomShape,
      }
    },
    [shapes.polygons],
  )

  /**
   * Get shape's positions in Vector3 format.
   *
   * @param shapeId Shape ID
   * @param shape Shape data
   */
  const getShapePointsV3 = useCallback((shape: Polygon): Vector3[] => {
    const { shape_id: shapeId } = shape
    if (shapePointsV3Ref.current[shapeId]) return shapePointsV3Ref.current[shapeId]

    const points = shape.positions.map((pos) => new Vector3(...pos))
    shapePointsV3Ref.current[shapeId] = points
    return points
  }, [])

  /**
   * Get Three.js Plane instance for a shape.
   *
   * @param shapeId Shape ID
   * @param shape Shape data
   */
  const getShapePlane = useCallback(
    (shape: Polygon): Plane => {
      const { shape_id: shapeId } = shape
      if (threePlanesRef.current[shapeId]) return threePlanesRef.current[shapeId]

      const points = getShapePointsV3(shape)
      const plane = new Plane().setFromCoplanarPoints(points[0], points[1], points[2])
      threePlanesRef.current[shapeId] = plane
      return plane
    },
    [getShapePointsV3],
  )

  /**
   * Generate vectors pointing towards the edge of the minimum boundary to determine the up/right/forward direction.
   * The definition of up/right/forward is based on the camera, either from the camera profile or the arcball controls.
   */
  const getEdgeVectors = useCallback(
    (volumeId: string, topShapePolygon: Polygon, bottomShapePolygon: Polygon): EdgeVector | null => {
      let cameraProfileState: CameraProfileState | undefined
      if (cameraProfile) {
        cameraProfileState = cameraProfile.state
      } else if (cameraRef.current && arcballControlsRef.current) {
        const camProfile = getCameraProfile(cameraRef.current, arcballControlsRef.current)
        if (camProfile) cameraProfileState = camProfile.state
      }

      if (!cameraProfileState) return null

      // Convert data to Vec3 for use later
      const topShapePoints = getShapePointsV3(topShapePolygon)
      const bottomShapePoints = getShapePointsV3(bottomShapePolygon)

      // Construct plane instances for top and bottom planes
      const topPlane = getShapePlane(topShapePolygon)

      // Get direction vector from top to bottom plane. Might look weird but top plane's normal could be facing upward
      // due to how it was drawn so we need do some checking and inverting if necessary.
      const direction = findNormalTowardsB(topShapePoints[0], bottomShapePoints[0], topPlane.normal)

      // Get camera vectors for up/right direction reference
      const cameraMatrix = new Matrix4().fromArray(cameraProfileState.arcballState.cameraMatrix.elements)
      const cameraRotation = new Quaternion().setFromRotationMatrix(cameraMatrix)
      const cameraRightVector = new Vector3(1, 0, 0).applyQuaternion(cameraRotation)
      const cameraUpVector = new Vector3(0, 1, 0).applyQuaternion(cameraRotation)

      // Get top boundary for the actual up/right direction
      const topBoundary = minimumRectangleBoundaries.find(
        (bundle) => bundle.polygon.shape_id === topShapePolygon?.shape_id,
      )?.boundary
      if (!topBoundary) return null

      // Calculate up/right direction vectors
      const positions = transform2Dto3D(
        topBoundary.vertices.map((vertex) => new Vector2(...vertex)),
        topShapePolygon.transformation,
      ).map((v) => new Vector3(...v))
      const vectors = calculateBoundaryDirectionVectors(positions, cameraUpVector, cameraRightVector)
      if (!vectors) return null

      // Calculate center
      const center = getCentroidOfPolygon3d(positions)

      // make Matrix4 from vectors
      const { up, right } = vectors
      const transform = new Matrix4().makeBasis(right.direction, up.direction, direction).setPosition(center)

      // Calculate distance between top and bottom planes
      const topBottomDistance = new Vector3(...topShapePolygon.center!).distanceTo(
        new Vector3(...bottomShapePolygon.center!),
      )

      return {
        center,
        up,
        right,
        forward: {
          direction,
          point: center.clone().add(direction.clone().multiplyScalar(topBottomDistance)),
          distance: topBottomDistance,
        },
        transform,
      }
    },
    [minimumRectangleBoundaries, cameraProfile, cameraRef, arcballControlsRef, getShapePlane, getShapePointsV3],
  )

  /**
   * Generate working grid points, skipping if locked.
   */
  useEffect(() => {
    Object.keys(intervals).forEach((volumeId) => {
      const interval = intervals[volumeId]

      const { topShape, bottomShape } = getTopAndBottomShapes(interval)
      if (!topShape || !bottomShape) return

      // locked grid does not need to generate grid points
      if (interval.locked) return

      // Validate config values. Don't generate grid points if the values are invalid.
      if (!validateIntervalConfig(interval)) return

      // Get edge vectors for the polygon
      const edgeVector = getEdgeVectors(volumeId, topShape, bottomShape)
      if (!edgeVector) return

      // Construct plane instances for top and bottom planes
      const topPlane = getShapePlane(topShape)
      const bottomPlane = getShapePlane(bottomShape)

      let longAxis: IntervalsAxisConfig
      let shortAxis: IntervalsAxisConfig
      const axisA = {
        ...interval.longAxis,
        value: Math.max(interval.longAxis.interval.value, GRID_POINT_MINIMUM_INTERVAL),
      }
      const axisB = {
        ...interval.shortAxis,
        value: Math.max(interval.shortAxis.interval.value, GRID_POINT_MINIMUM_INTERVAL),
      }

      if (edgeVector.right.distance > edgeVector.up.distance) {
        longAxis = axisA
        shortAxis = axisB
      } else {
        longAxis = axisB
        shortAxis = axisA
      }

      // Generate grid points
      const points = generateGridPoints(
        {
          ...interval,
          longAxis,
          shortAxis,
        },
        true,
      ).map((point) => [millimeterToMeter(point[0]), millimeterToMeter(point[1])])

      // convert 2D to 3D
      const points3d = points.map((point) => new Vector3(point[0], point[1], 0).applyMatrix4(edgeVector.transform))
      const topPlanePoints = getShapePointsV3(topShape)
      const bottomPlanePoints = getShapePointsV3(bottomShape)

      // Finalize grid points by filtering out points that are not within the top or bottom plane's polygon.
      const gridPoints = points3d
        // filter out points that are not on the top plane
        .reduce<{ id: string; points: [PointArray, PointArray] }[]>((arr, point) => {
          // Generated points are for top plane, so let's check that first
          if (!isPointInsidePolygon3D(point, topPlanePoints, topPlane.normal)) {
            return arr
          }

          // Check for bottom plane as well
          const bottomPoint = bottomPlane.projectPoint(point, new Vector3())
          if (!isPointInsidePolygon3D(bottomPoint, bottomPlanePoints, bottomPlane.normal)) {
            return arr
          }

          arr.push({
            id: crypto.randomUUID(),
            points: [
              [point.x, point.y, point.z],
              [bottomPoint.x, bottomPoint.y, bottomPoint.z],
            ],
          })

          return arr
        }, [])

      dispatch(updateWorkingGridPoints({ volumeId, points: gridPoints }))

      // Generate edge planes for edge-based grids
      if (interval.type === GridType.EdgeBased && topShape) {
        // Get top boundary for reference
        const topBoundary = minimumRectangleBoundaries.find(
          (bundle) => bundle.polygon.shape_id === topShape.shape_id,
        )?.boundary
        if (!topBoundary) return

        const edgePlanes = generateEdgePlanes(interval, topBoundary, topShape)

        dispatch(
          updateWorkingEdgePlanes({
            id: volumeId,
            edgePlanes,
          }),
        )
      }
    })
  }, [
    intervals,
    minimumRectangleBoundaries,
    getEdgeVectors,
    getShapePlane,
    getShapePointsV3,
    getTopAndBottomShapes,
    dispatch,
  ])

  /**
   * Generate label distance from grid points.
   */
  useEffect(() => {
    const labels = Object.keys(workingGridPoints).map(
      (volumeId) =>
        workingGridPoints[volumeId]
          .map<PolylineProps | null>(({ id, points }, pointIndex) => {
            // Filter out invalid points.
            if (!points[0] || !points[1]) return null

            // Figure out top shape for the volume
            const inspectionItem = inspectionItems.find((item) => item.inspection_item_id === volumeId)
            const topShape = inspectionItem ? findUpperPlane(shapes, inspectionItem) : null
            const bottomShape = inspectionItem ? findLowerPlane(shapes, inspectionItem) : null

            let bottomPlane: Plane | null = null
            let topPlane: Plane | null = null
            if (bottomShape) bottomPlane = getShapePlane(bottomShape)
            if (topShape) topPlane = getShapePlane(topShape)

            return {
              id,
              points,
              anchorScale: volumeId === highlightedWorkingGrid ? 1.5 : undefined,
              label: {
                visible: true,
                label: `t${pointIndex + 1}`,
                styles: {
                  borderColor: volumeId === highlightedWorkingGrid ? '#fff' : undefined,
                },
              },
              lineOutline: {},
              visible:
                !hiddenElementIds.includes(`panel-grid-${volumeId}`) &&
                !hiddenElementIds.includes(`panel-grid-point-${volumeId}-${pointIndex}`),
              hideAnchors: false,
              isInteractable: false,
              isEditing: isEditing(id),
              vertexAnchors: {
                isInteractable: true,
                onDown(anchorIndex) {
                  // Limit anchor placement on top plane only
                  if (anchorIndex === 0 && topShape) {
                    pointSelectionObjectIdsRef.current = [topShape.shape_id]
                    dimmedShapeIdRef.current = topShape.shape_id
                    dispatch(addDimmedElementId(topShape.shape_id))
                  } else if (anchorIndex === 1 && bottomShape) {
                    pointSelectionObjectIdsRef.current = [bottomShape.shape_id]
                    dimmedShapeIdRef.current = bottomShape.shape_id
                    dispatch(addDimmedElementId(bottomShape.shape_id))
                  }
                },
                onUpdated: (pts) => {
                  dispatch(
                    updateWorkingGridPoint({
                      volumeId,
                      pointIndex,
                      points: pts as [PointArray, PointArray],
                    }),
                  )

                  pointSelectionObjectIdsRef.current = []
                },
                getPoints(currentPosition, anchorIndex) {
                  // Sometimes we get current position not on the plane (eg: from PCD instead).
                  // So we need to skip those.
                  const onTopPlane = topPlane
                    ? Math.abs(topPlane.distanceToPoint(new Vector3(...currentPosition))) < 1e-6
                    : false
                  const onBottomPlane = bottomPlane
                    ? Math.abs(bottomPlane.distanceToPoint(new Vector3(...currentPosition))) < 1e-6
                    : false

                  // Anchor of top plane was adjusted, so project the point onto the bottom plane
                  if (anchorIndex === 0) {
                    if (!bottomPlane || !onTopPlane) {
                      return null
                    }

                    return [
                      currentPosition,
                      bottomPlane.projectPoint(new Vector3(...currentPosition), new Vector3()).toArray(),
                    ]
                  }

                  // Otherwise, anchor of bottom plane was adjusted, so project the point onto the top plane
                  if (!topPlane || !onBottomPlane) {
                    return null
                  }

                  return [
                    topPlane.projectPoint(new Vector3(...currentPosition), new Vector3()).toArray(),
                    currentPosition,
                  ]
                },
              },
              topAnchorProps: {
                color: '#88DF41',
              },
            }
          })
          .filter(Boolean) as PolylineProps[],
    )

    const workingLabels = labels.reduce(
      (acc, current, index) => {
        acc[`${index}`] = current
        return acc
      },
      {} as Record<string, PolylineProps[]>,
    )
    setWorkingDistanceLabels(workingLabels)
  }, [
    shapes,
    inspectionItems,
    workingGridPoints,
    highlightedWorkingGrid,
    hiddenElementIds,
    pointSelectionObjectIdsRef,
    getShapePlane,
    isEditing,
    dispatch,
  ])

  /**
   * Reset anchor placement objects when tool is deselected.
   */
  useEffect(() => {
    if (prevSelectedTool === EDITOR_TOOLS.GRID && !isToolSelected) {
      pointSelectionObjectIdsRef.current = []
    }
  }, [isToolSelected, prevSelectedTool, pointSelectionObjectIdsRef, dispatch])

  /**
   * For invalid grid points, only to be shown when an axis is being edited
   */
  const invalidPoints = useMemo(
    (): CircleAnchorProps[] =>
      Object.keys(workingGridPoints)
        .map((volumeId) =>
          workingGridPoints[volumeId]
            // Only invalid points
            .filter(({ points }) => points.length && (points.length <= 1 || !points[0] || !points[1]))
            .map<CircleAnchorProps>(({ points }, index) => ({
              id: `invalid-grid-point-${volumeId}-${index}`,
              point: points.filter(Boolean)[0], // The valid point can be on either 0 or 1 index
              color: 'orange',
              opacity: shownWorkingEdgePlanes?.id === volumeId ? 0.7 : undefined,
              visible: shownWorkingEdgePlanes?.id === volumeId,
            })),
        )
        .flat(),
    [workingGridPoints, shownWorkingEdgePlanes],
  )

  return {
    objects: {
      circleAnchors: [...invalidPoints],
      labels: warningLabels,
      polylines: [...Object.values(existingDistanceLabels).flat(), ...Object.values(workingDistanceLabels).flat()],
      polygonPlaneMeshes: useMemo(
        () => [
          // Minimum boundary (needed for placing grid points, not needed at all times or displayed to user)
          ...(isToolSelected
            ? (shapes?.polygons
                .filter((polygon) => polygon.plane_side === PlaneSide.UPPER)
                .map((polygon) => {
                  const topBoundary = minimumRectangleBoundaries.find(
                    (bundle) => bundle.polygon.shape_id === polygon?.shape_id,
                  )?.boundary

                  if (!topBoundary) return null

                  return {
                    polygon: {
                      shape_id: `minimum-boundary-${polygon.shape_id}`,
                      positions: transform2Dto3D(
                        topBoundary.vertices.map((vertex) => new Vector2(...vertex)),
                        polygon.transformation,
                      ),
                      vertices: topBoundary.vertices,
                      center: transform2Dto3D(
                        [new Vector2(topBoundary.center[0], topBoundary.center[1])],
                        polygon.transformation,
                      )[0],
                    } as Polygon,
                    color: 'pink',
                    opacity: 0,
                    selectable: false,
                  }
                })
                .filter(Boolean) as PolygonPlaneMeshProps[]) || []
            : []),
          // Edge planes
          ...(Object.keys(workingEdgePlanes || {})
            .map<PolygonPlaneMeshProps[] | null>((volumeId) => {
              const points = workingEdgePlanes[volumeId]
              if (!points) return null

              const shape = shapes.polygons?.find((poly) => poly.shape_id === points.shapeId)
              if (!shape) return null

              const plane = threePlanesRef.current[shape.shape_id]
              if (!plane) return null

              // An important thing to remember, the edge planes are labelled by their placement, either on the long or short axis.
              // However, which side to be shown depends if the _other_ side is being edited.
              // eg: if long axis is being edited, it is the edge planes on the short axis that should be shown.
              const isLongShown =
                shownWorkingEdgePlanes &&
                shownWorkingEdgePlanes.id === volumeId &&
                shownWorkingEdgePlanes.axis === 'shortAxis'
              const isShortShown =
                shownWorkingEdgePlanes &&
                shownWorkingEdgePlanes.id === volumeId &&
                shownWorkingEdgePlanes.axis === 'longAxis'

              return [
                ...points.shortAxis.map((vertices, index) => {
                  const positions = transform2Dto3D(
                    vertices.map((v) => new Vector2(...v)),
                    shape.transformation,
                  )
                    .map((p) => new Vector3(...p))
                    .map((v) => v.add(plane.normal.clone().negate().multiplyScalar(0.0001)))
                  const center = getCentroidOfPolygon3d(positions).toArray()

                  positions.push(positions[0].clone())
                  return {
                    id: `edge-plane-${volumeId}-short-axis-${index}`,
                    polygon: {
                      shape_id: `edge-plane-${volumeId}-short-axis-${index}`,
                      positions: positions.map((p) => p.toArray()),
                      vertices,
                      center,
                      transformation: shape.transformation,
                      min_rect_boundaries: {} as MinimumAreaBoundary,
                    },
                    color: '#48BB78',
                    opacity: isShortShown ? 0.5 : 0,
                    selectable: false,
                    depthWrite: false,
                    depthTest: false,
                    renderOrder: 2,
                    showGuides: true,
                    showGuidesLabel: false,
                    guideColor: '#9AE6B4',
                    guideOpacity: 1,
                    guideStyle: LineStyle.Solid,
                    guideThickness: 0.005,
                  }
                }),
                ...points.longAxis.map((vertices, index) => {
                  const positions = transform2Dto3D(
                    vertices.map((v) => new Vector2(...v)),
                    shape.transformation,
                  )
                    .map((p) => new Vector3(...p))
                    .map((v) => v.add(plane.normal.clone().negate().multiplyScalar(0.0001)))
                  const center = getCentroidOfPolygon3d(positions).toArray()

                  positions.push(positions[0].clone())
                  return {
                    id: `edge-plane-${volumeId}-long-axis-${index}`,
                    polygon: {
                      shape_id: `edge-plane-${volumeId}-long-axis-${index}`,
                      positions: positions.map((p) => p.toArray()),
                      vertices,
                      center,
                      transformation: shape.transformation,
                      min_rect_boundaries: {} as MinimumAreaBoundary,
                    },
                    color: '#48BB78',
                    opacity: isLongShown ? 0.5 : 0,
                    selectable: false,
                    depthWrite: false,
                    depthTest: false,
                    renderOrder: 2,
                    showGuides: true,
                    showGuidesLabel: false,
                    guideColor: '#9AE6B4',
                    guideOpacity: 1,
                    guideStyle: LineStyle.Solid,
                    guideThickness: 0.005,
                  }
                }),
              ]
            })
            .flat()
            .filter(Boolean) as PolygonPlaneMeshProps[]),
        ],
        [shapes, minimumRectangleBoundaries, workingEdgePlanes, isToolSelected, shownWorkingEdgePlanes],
      ),
    },
    events: {
      onMouseUp() {
        if (!isToolSelected || !dimmedShapeIdRef.current) return

        dispatch(removeDimmedElementId(dimmedShapeIdRef.current))
        dimmedShapeIdRef.current = undefined
      },
    },
  }
}

export default useMainCanvas
